// /src/components/InvitationManagement.js

import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { 
  FaUserPlus, 
  FaCheck, 
  FaSpinner,
  FaBuilding,
  FaTimes,
  FaExclamationTriangle,
  FaCalendar,
  FaUser
} from 'react-icons/fa';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const InvitationCard = ({ invitation, onAccept, onDeny }) => {
  const { t } = useTranslation();

  return (
    <Card className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-all duration-300 mb-4">
      <CardContent className="p-6">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-medium text-white">
                {invitation.property.name}
              </h4>
              <p className="text-sm text-gray-400">
                {t('invitations.invitedBy')}: {invitation.inviter.username}
              </p>
              <p className="text-sm text-gray-500">
                {t('invitations.expires')}: {new Date(invitation.expiresAt).toLocaleDateString()}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => onDeny(invitation._id)}
                className="px-4 py-2 bg-red-600/10 text-red-500 rounded-lg 
                         hover:bg-red-600/20 transition-colors"
              >
                {t('invitations.actions.deny')}
              </button>
              <button
                onClick={() => onAccept(invitation._id)}
                className="px-4 py-2 bg-green-600/10 text-green-500 rounded-lg 
                         hover:bg-green-600/20 transition-colors"
              >
                {t('invitations.actions.accept')}
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SendInviteForm = ({ properties, onSend, isSending }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchUsers = async (term) => {
    if (term.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get(`/users/search?term=${term}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedUser(null);
    setShowDropdown(true);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchUsers(value);
    }, 300);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.username);
    setShowDropdown(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedUser && selectedProperty) {
      onSend(selectedUser.username, selectedProperty);
      setSearchTerm('');
      setSelectedUser(null);
      setSelectedProperty('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Search User
        </label>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-3 py-2 pl-10 bg-gray-700 border border-gray-600 rounded-lg 
                     text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            placeholder="Search by username..."
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            {isSearching ? (
              <FaSpinner className="animate-spin text-gray-400" />
            ) : (
              <FaUser className="text-gray-400" />
            )}
          </div>
          {selectedUser && (
            <button
              type="button"
              onClick={() => {
                setSelectedUser(null);
                setSearchTerm('');
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 
                       hover:text-gray-300"
            >
              <FaTimes />
            </button>
          )}
        </div>

        {showDropdown && searchResults.length > 0 && (
          <div className="absolute w-full mt-1 bg-gray-700 border border-gray-600 
                        rounded-lg shadow-lg z-50">
            {searchResults.map((user) => (
              <button
                key={user._id}
                type="button"
                onClick={() => handleUserSelect(user)}
                className="w-full px-4 py-2 text-left text-white hover:bg-gray-600 
                         first:rounded-t-lg last:rounded-b-lg flex items-center"
              >
                <FaUser className="text-gray-400 mr-2" />
                <div>
                  <div className="font-medium">{user.username}</div>
                  {user.fullName && (
                    <div className="text-sm text-gray-400">{user.fullName}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Select Property
        </label>
        <select
          value={selectedProperty}
          onChange={(e) => setSelectedProperty(e.target.value)}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                   text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          required
        >
          <option value="">Select a property</option>
          {properties.map(property => (
            <option key={property._id} value={property._id}>
              {property.name}
            </option>
          ))}
        </select>
      </div>

      <button
        type="submit"
        disabled={isSending || !selectedUser || !selectedProperty}
        className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                 transition-colors duration-200 flex items-center justify-center disabled:opacity-50 
                 disabled:cursor-not-allowed"
      >
        {isSending ? (
          <>
            <FaSpinner className="animate-spin mr-2" />
            Sending...
          </>
        ) : (
          <>
            <FaUser className="mr-2" />
            Send Invitation
          </>
        )}
      </button>
    </form>
  );
};

const SentInvitationsSection = ({ sentInvitations, onCancelInvitation }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-white mb-4">
        {t('invitations.sections.sent.title')}
      </h3>
      {!sentInvitations || sentInvitations.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6 text-center">
            <p className="text-gray-400">{t('invitations.sections.sent.empty')}</p>
          </CardContent>
        </Card>
      ) : (
        sentInvitations.map((invitation) => {
          return (
            <Card key={invitation._id} className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">{invitation.username}</div>
                    <div className="text-sm text-gray-400">
                      {invitation.property.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {t('invitations.expires')}: {new Date(invitation.expiresAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => onCancelInvitation(invitation._id)}
                    className="px-3 py-1.5 bg-red-600/10 text-red-500 rounded-lg hover:bg-red-600/20 
                             transition-colors flex items-center space-x-2"
                  >
                    <FaTimes />
                    <span>{t('invitations.actions.cancel')}</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

const PropertyManagersSection = ({ properties, onRemoveManager }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-white mb-4">
        {t('invitations.sections.managers.title')}
      </h3>
      {properties.map((property) => (
        <Card key={property._id} className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">{property.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {property.managers && property.managers.length > 0 ? (
              property.managers.map((manager) => (
                <div key={manager._id} className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FaUser className="text-gray-400" />
                    <div>
                      <div className="text-white">{manager.username}</div>
                      <div className="text-sm text-gray-400">{manager.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => onRemoveManager(property._id, manager._id)}
                    className="px-3 py-1.5 bg-red-600/10 text-red-500 rounded-lg hover:bg-red-600/20 transition-colors flex items-center space-x-2"
                  >
                    <FaTimes />
                    <span>{t('invitations.actions.removeManager')}</span>
                  </button>
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-center py-4">{t('invitations.sections.managers.noManagers')}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const PendingInvitationsSection = ({ pendingInvitations, onAccept, onDeny }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-white mb-4">
        {t('invitations.sections.pending.title')}
      </h3>
      {pendingInvitations.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6 text-center">
            <p className="text-gray-400">{t('invitations.sections.pending.empty')}</p>
          </CardContent>
        </Card>
      ) : (
        pendingInvitations.map((invitation) => (
          <InvitationCard
            key={invitation._id}
            invitation={invitation}
            onAccept={onAccept}
            onDeny={onDeny}
          />
        ))
      )}
    </div>
  );
};

const InvitationManagement = () => {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create a single function to fetch all data
  const fetchAllData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [propertiesRes, pendingRes, sentRes] = await Promise.all([
        api.get('/property/user/current', {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        }),
        api.get('/invitation/pending', {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        }),
        api.get('/invitation/sent', {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        })
      ]);

      setProperties(propertiesRes.data.owned);
      setPendingInvitations(pendingRes.data);
      setSentInvitations(sentRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError(t('invitations.messages.error.fetchData'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleSendInvitation = async (inviteeUsername, propertyId) => {
    setIsSending(true);
    try {
      await api.post(
        '/invitation/send', 
        { propertyId, inviteeUsername },
        { headers: { 'x-auth-token': localStorage.getItem('token') } }
      );
      toast.success(t('invitations.messages.sendSuccess'));
      // Only fetch sent invitations after sending a new one
      const sentRes = await api.get('/invitation/sent', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setSentInvitations(sentRes.data);
    } catch (error) {
      toast.error(error.response?.data?.error || t('invitations.messages.error.send'));
    } finally {
      setIsSending(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await api.delete(`/invitation/cancel/${invitationId}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      toast.success(t('invitations.messages.cancelSuccess'));
      // Only fetch sent invitations after canceling
      const sentRes = await api.get('/invitation/sent', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setSentInvitations(sentRes.data);
    } catch (error) {
      toast.error(error.response?.data?.error || t('invitations.messages.error.cancel'));
    }
  };

  const handleDenyInvitation = async (invitationId) => {
    try {
      await api.post('/invitation/deny', 
        { invitationId },
        { headers: { 'x-auth-token': localStorage.getItem('token') } }
      );
      toast.success(t('invitations.messages.denySuccess'));
      // Only fetch pending invitations after denying
      const pendingRes = await api.get('/invitation/pending', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setPendingInvitations(pendingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.error || t('invitations.messages.error.deny'));
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      await api.post(
        '/invitation/accept',
        { invitationId },
        { headers: { 'x-auth-token': localStorage.getItem('token') } }
      );
      toast.success(t('invitations.messages.acceptSuccess'));
      // Only fetch pending invitations and properties after accepting
      const [pendingRes, propertiesRes] = await Promise.all([
        api.get('/invitation/pending', {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        }),
        api.get('/property/user/current', {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        })
      ]);
      setPendingInvitations(pendingRes.data);
      setProperties(propertiesRes.data.owned);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to accept invitation';
      toast.error(t(`invitations.messages.error.${errorMessage}`) || errorMessage);
    }
  };

  const handleRemoveManager = async (propertyId, managerId) => {
    try {
      await api.post('/invitation/remove-manager',
        { propertyId, managerId },
        { headers: { 'x-auth-token': localStorage.getItem('token') } }
      );
      toast.success(t('invitations.messages.removeManagerSuccess'));
      // Only fetch properties after removing a manager
      const propertiesRes = await api.get('/property/user/current', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setProperties(propertiesRes.data.owned);
    } catch (error) {
      toast.error(error.response?.data?.error || t('invitations.messages.error.removeManager'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <FaSpinner className="animate-spin text-indigo-500 text-4xl" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-8 p-8 pt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-white">{t('invitations.title')}</h2>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center">
          <FaExclamationTriangle className="text-red-500 mr-2" />
          <span className="text-red-500">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-400"
          >
            <FaTimes />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div>
          <SendInviteForm
            properties={properties}
            onSend={handleSendInvitation}
            isSending={isSending}
          />

          {/* Sent Invitations Section */}
          <div className="mt-8">
            <SentInvitationsSection
              sentInvitations={sentInvitations}
              onCancelInvitation={handleCancelInvitation}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          <PendingInvitationsSection
            pendingInvitations={pendingInvitations}
            onAccept={handleAcceptInvitation}
            onDeny={handleDenyInvitation}
          />

          <PropertyManagersSection
            properties={properties}
            onRemoveManager={handleRemoveManager}
          />
        </div>
      </div>
    </div>
  );
};

export default InvitationManagement;